package com.videoanalyzer.web.components.analysis

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.config.Config
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Recommendations section components.
 */

@Composable
fun RecommendationsCard(
    recommendations: List<String>,
    showFull: Boolean = true,
    freeLimit: Int = 2,
    totalCount: Int = 0,
    onUpgrade: () -> Unit = {}
) {
    if (recommendations.isEmpty()) return

    val visibleRecommendations = if (showFull) recommendations else recommendations.take(freeLimit)
    val hiddenCount = if (showFull) 0 else (recommendations.size - freeLimit).coerceAtLeast(0)

    Div({
        classes("card", "fade-in-up", "stagger-3")
        style { marginTop(24.px) }
    }) {
        // Header with Quick Wins branding
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(20.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                Div({
                    style {
                        width(44.px)
                        height(44.px)
                        borderRadius(8.px)
                        backgroundColor(Color("#27272A"))
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        fontSize(22.px)
                    }
                }) { Text("⚡") }

                Div {
                    H3({
                        style {
                            fontSize(18.px)
                            fontWeight(700)
                            color(Color("#FAFAFA"))
                            marginBottom(2.px)
                        }
                    }) { Text("Quick Wins") }
                    P({
                        style {
                            fontSize(12.px)
                            color(Color("#71717A"))
                        }
                    }) { Text("Minimum effort, maximum impact") }
                }
            }

            // Legend
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                LegendItem("⚡", "Instant")
                LegendItem("🔧", "Quick fix")
            }
        }

        // Recommendations list
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(10.px)
            }
        }) {
            visibleRecommendations.forEachIndexed { index, rec ->
                QuickWinItem(index + 1, rec)
            }
        }

        // Gated recommendations upgrade CTA
        if (!showFull && hiddenCount > 0) {
            Div({
                style {
                    marginTop(16.px)
                    padding(20.px)
                    backgroundColor(Color("rgba(14, 165, 233, 0.1)"))
                    borderRadius(8.px)
                    property("border", "1px dashed rgba(14, 165, 233, 0.4)")
                    textAlign("center")
                }
            }) {
                // Blurred preview
                Div({ style { marginBottom(16.px) } }) {
                    repeat(hiddenCount.coerceAtMost(3)) { i ->
                        Div({
                            style {
                                padding(10.px)
                                marginBottom(8.px)
                                backgroundColor(Color("#27272A"))
                                borderRadius(6.px)
                                property("filter", "blur(4px)")
                                color(Color("#71717A"))
                                fontSize(14.px)
                            }
                        }) {
                            Text("Recommendation ${freeLimit + i + 1}: Unlock to see this tip...")
                        }
                    }
                }

                // Upgrade CTA
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        gap(12.px)
                    }
                }) {
                    Span({ style { fontSize(24.px) } }) { Text("🔒") }

                    P({
                        style {
                            color(Color("#0EA5E9"))
                            fontSize(16.px)
                            fontWeight(600)
                            marginBottom(4.px)
                        }
                    }) { Text("$hiddenCount more recommendations available") }

                    P({
                        style {
                            color(Color("#A1A1AA"))
                            fontSize(13.px)
                            marginBottom(12.px)
                        }
                    }) { Text("Upgrade to Pro to unlock all insights and maximize your CTR") }

                    Button({
                        classes("btn", "btn-primary")
                        style {
                            padding(12.px, 32.px)
                            fontSize(15.px)
                            fontWeight(600)
                        }
                        onClick { onUpgrade() }
                    }) {
                        Span({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                            }
                        }) {
                            Text("Unlock All Recommendations")
                            Span({ style { fontSize(18.px) } }) { Text("→") }
                        }
                    }

                    P({
                        style {
                            color(Color("#71717A"))
                            fontSize(12.px)
                            marginTop(8.px)
                        }
                    }) { Text("Starting from ${Config.CURRENCY_SYMBOL}${Config.STARTING_PRICE}") }
                }
            }
        }
    }
}

@Composable
fun LegendItem(icon: String, label: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            backgroundColor(Color("#27272A"))
            padding(4.px, 8.px)
            borderRadius(4.px)
        }
    }) {
        Span({ style { fontSize(12.px) } }) { Text(icon) }
        Span({
            style {
                fontSize(11.px)
                color(Color("#71717A"))
            }
        }) { Text(label) }
    }
}

@Composable
fun QuickWinItem(number: Int, text: String) {
    val isInstant = text.contains("⚡")
    val isQuickFix = text.contains("🔧")
    val priority = when {
        isInstant -> "instant"
        isQuickFix -> "quick"
        else -> "normal"
    }

    val borderColor = when (priority) {
        "instant" -> "#22C55E"
        "quick" -> "#0EA5E9"
        else -> "#3F3F46"
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            gap(14.px)
            padding(14.px, 16.px)
            backgroundColor(Color("#27272A"))
            borderRadius(8.px)
            property("border-left", "3px solid $borderColor")
        }
    }) {
        // Priority number badge
        Div({
            style {
                width(28.px)
                height(28.px)
                borderRadius(6.px)
                backgroundColor(Color(when (priority) {
                    "instant" -> "#22C55E"
                    "quick" -> "#0EA5E9"
                    else -> "#3F3F46"
                }))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                color(Color.white)
                fontWeight(700)
                fontSize(13.px)
                property("flex-shrink", "0")
            }
        }) { Text("$number") }

        // Content
        Div({ style { property("flex", "1") } }) {
            Span({
                style {
                    color(Color("#FAFAFA"))
                    fontSize(14.px)
                    lineHeight("1.5")
                }
            }) { Text(text) }
        }

        // Priority indicator for instant wins
        if (priority == "instant") {
            Div({
                style {
                    backgroundColor(Color("rgba(34, 197, 94, 0.2)"))
                    color(Color("#22C55E"))
                    padding(4.px, 8.px)
                    borderRadius(4.px)
                    fontSize(10.px)
                    fontWeight(600)
                    property("text-transform", "uppercase")
                    letterSpacing(0.5.px)
                    property("white-space", "nowrap")
                }
            }) { Text("Do First") }
        }
    }
}
